import { RepeatWrapping } from 'three'
import { checkWebPSupport } from 'supports-webp-sync'
import EventEmitter from './EventEmitter'
import Loaders from './Loaders'

// Textures PNG
import hatchlightPng from '../../static/3d_textures/png/hatchlight.png'
import hatchdarkPng from '../../static/3d_textures/png/hatchdark.png'
import alphaTexturePng from '../../static/3d_textures/png/alpha_map.png'
import fieldsTexturePng from '../../static/3d_textures/png/fields_texture.png'
import bottleMalvaColorFonioPng from '../../static/3d_textures/png/bottle_malva_color_fonio.png'
import bottleMalvaColorGentilePng from '../../static/3d_textures/png/bottle_malva_color_gentile.png'
import bottleMalvaColorGemmaPng from '../../static/3d_textures/png/bottle_malva_color_gemma.png'
import bottleMalvaColorEmyPng from '../../static/3d_textures/png/bottle_malva_color_emy.png'
import bottleMalvaOpacityPng from '../../static/3d_textures/png/bottle_malva_opacity.png'
import bottlePassitoColorEosPng from '../../static/3d_textures/png/bottle_passito_color_eos.png'
import bottlePassitoOpacityPng from '../../static/3d_textures/png/bottle_passito_opacity.png'
import bottleSpumanteColorArsPng from '../../static/3d_textures/png/bottle_spumante_color_ars.png'
import bottleSpumanteOpacityPng from '../../static/3d_textures/png/bottle_spumante_opacity.png'
import bottleRossoColorDuemondiPng from '../../static/3d_textures/png/bottle_rosso_color_duemondi.png'
import bottleRossoOpacityPng from '../../static/3d_textures/png/bottle_rosso_opacity.png'
import cloudsPng from '../../static/3d_textures/png/clouds.png'
import whiteTexturePng from '../../static/3d_textures/png/white_texture.png'

// Textures WebP
import hatchlightWebp from '../../static/3d_textures/webp/hatchlight.webp'
import hatchdarkWebp from '../../static/3d_textures/webp/hatchdark.webp'
import alphaTextureWebp from '../../static/3d_textures/webp/alpha_map.webp'
import fieldsTextureWebp from '../../static/3d_textures/webp/fields_texture.webp'
import bottleMalvaColorFonioWebp from '../../static/3d_textures/webp/bottle_malva_color_fonio.webp'
import bottleMalvaColorGentileWebp from '../../static/3d_textures/webp/bottle_malva_color_gentile.webp'
import bottleMalvaColorGemmaWebp from '../../static/3d_textures/webp/bottle_malva_color_gemma.webp'
import bottleMalvaColorEmyWebp from '../../static/3d_textures/webp/bottle_malva_color_emy.webp'
import bottleMalvaOpacityWebp from '../../static/3d_textures/webp/bottle_malva_opacity.webp'
import bottlePassitoColorEosWebp from '../../static/3d_textures/webp/bottle_passito_color_eos.webp'
import bottlePassitoOpacityWebp from '../../static/3d_textures/webp/bottle_passito_opacity.webp'
import bottleSpumanteColorArsWebp from '../../static/3d_textures/webp/bottle_spumante_color_ars.webp'
import bottleSpumanteOpacityWebp from '../../static/3d_textures/webp/bottle_spumante_opacity.webp'
import bottleRossoColorDuemondiWebp from '../../static/3d_textures/webp/bottle_rosso_color_duemondi.webp'
import bottleRossoOpacityWebp from '../../static/3d_textures/webp/bottle_rosso_opacity.webp'
import cloudsWebp from '../../static/3d_textures/webp/clouds.webp'
import whiteTextureWebp from '../../static/3d_textures/webp/white_texture.webp'

// Models
import layout from '../../static/3d_models/layout_DRACO.glb'
import bottleMalva from '../../static/3d_models/bottle_malva_web_DRACO.glb'
import bottlePassito from '../../static/3d_models/bottle_passito_web_DRACO.glb'
import bottleSpumante from '../../static/3d_models/bottle_spumante_web_DRACO.glb'
import bottleRosso from '../../static/3d_models/bottle_rosso_web_DRACO.glb'

// Fonts
import ebGaramond from '../../static/3d_fonts/EB_Garamond_Regular.json'

export default class Resources extends EventEmitter
{
    constructor()
    {
        super()
        this.loader = new Loaders()
        this.items = {}

        this.loader.on('fileEnd', (_resource, _data) =>
        {
            this.items[_resource.name] = _data

            // Manage texture
            if(_resource.type === this.loader.Types.Texture)
            {
                _data.flipY = false
                _data.wrapS = RepeatWrapping
                _data.wrapT = RepeatWrapping
            }

            this.trigger('progress', [this.loader.loaded / this.loader.toLoad])
        })

        this.loader.on('end', () =>
        {
            this.trigger('ready')
        })
        
        this.webPSupport = checkWebPSupport()
    }

    static async init()
    {
        const r = new Resources()

        r.loader.load([
            // Textures
            { name: 'hatchlight', source: r.webPSupport ? hatchlightWebp : hatchlightPng },
            { name: 'hatchdark', source: r.webPSupport ? hatchdarkWebp : hatchdarkPng },
            { name: 'alphaTexture', source: r.webPSupport ? alphaTextureWebp : alphaTexturePng },
            { name: 'fieldsTexture', source: r.webPSupport ? fieldsTextureWebp : fieldsTexturePng },
            { name: 'bottleMalvaColorFonio', source: r.webPSupport ? bottleMalvaColorFonioWebp : bottleMalvaColorFonioPng },
            { name: 'bottleMalvaColorGentile', source: r.webPSupport ? bottleMalvaColorGentileWebp : bottleMalvaColorGentilePng },
            { name: 'bottleMalvaColorGemma', source: r.webPSupport ? bottleMalvaColorGemmaWebp : bottleMalvaColorGemmaPng },
            { name: 'bottleMalvaColorEmy', source: r.webPSupport ? bottleMalvaColorEmyWebp : bottleMalvaColorEmyPng },
            { name: 'bottleMalvaOpacity', source: r.webPSupport ? bottleMalvaOpacityWebp : bottleMalvaOpacityPng },
            { name: 'bottlePassitoColorEos', source: r.webPSupport ? bottlePassitoColorEosWebp : bottlePassitoColorEosPng },
            { name: 'bottlePassitoOpacity', source: r.webPSupport ? bottlePassitoOpacityWebp : bottlePassitoOpacityPng },
            { name: 'bottleSpumanteColorArs', source: r.webPSupport ? bottleSpumanteColorArsWebp : bottleSpumanteColorArsPng },
            { name: 'bottleSpumanteOpacity', source: r.webPSupport ? bottleSpumanteOpacityWebp : bottleSpumanteOpacityPng },
            { name: 'bottleRossoColorDuemondi', source: r.webPSupport ? bottleRossoColorDuemondiPng : bottleRossoColorDuemondiWebp},
            { name: 'bottleRossoOpacity', source: r.webPSupport ? bottleRossoOpacityWebp : bottleRossoOpacityPng },
            { name: 'clouds', source: r.webPSupport ? cloudsWebp : cloudsPng },
            { name: 'whiteTexture', source: r.webPSupport ? whiteTextureWebp : whiteTexturePng },

            // Models
            { name: 'layout', source: layout },
            { name: 'bottleMalva', source: bottleMalva },
            { name: 'bottlePassito', source: bottlePassito },
            { name: 'bottleSpumante', source: bottleSpumante },
            { name: 'bottleRosso', source: bottleRosso },

            // Font
            { name: 'ebGaramond', source: ebGaramond }
        ])

        return r
    }
}